import React, { useState } from 'react';

import { MoreHorizontal, ThumbsDown, ThumbsUp } from 'lucide-react';

import type { Board } from 'common/api/endpoints/boards';
import type { Post } from 'common/api/resources/posts';
import MergePostDropdown from 'common/post/MergePostDropdown';
import DropdownMenu from 'common/ui/DropdownMenu';
import Flex from 'common/ui/Flex';
import Popover from 'common/ui/Popover';
import VStack from 'common/ui/VStack';
import AlertDialog from 'common/uiV3/AlertDialog';
import Button from 'common/uiV3/Button';
import Tooltip from 'common/uiV3/Tooltip';
import type { QueueRow as Row } from '../types';

enum Action {
  approve = 'approve',
  merge = 'merge',
  delete = 'delete',
}

type Props = {
  row: Row;
  boards: Board[];
};

const getLabels = (row: Row) => {
  const isDuplicatePost = row.item.duplicatePost;
  const isCannyPost = row.item.source === 'canny';

  if (isDuplicatePost && isCannyPost) {
    return {
      action: 'Merge post?',
      positiveTooltip: 'Merge post',
      negativeTooltip: 'Do not merge',
      deleteModalCTA: 'Dismiss merge suggestion',
      deleteModalHeader: <>Are you sure you want to dismiss this merge&nbsp;suggestion?</>,
      deleteModalBody:
        'The suggestion will no longer be visible to you or other users in your workspace. This action cannot be reversed.',
    };
  }

  if (isDuplicatePost && !isCannyPost) {
    return {
      action: 'Merge post?',
      positiveTooltip: 'Merge as vote',
      negativeTooltip: 'Dismiss with no action',
      deleteModalCTA: 'Dismiss merge suggestion',
      deleteModalHeader: <>Are you sure you want to dismiss this merge&nbsp;suggestion?</>,
      deleteModalBody:
        'The suggestion will no longer be visible to you or other users in your workspace. This action cannot be reversed.',
    };
  }

  // no duplicate post
  if (isCannyPost) {
    return {
      action: 'Keep post?',
      positiveTooltip: 'Keep post',
      negativeTooltip: 'Delete post',
      deleteModalCTA: 'Delete post',
      deleteModalHeader: 'Are you sure you want to delete this post?',
      deleteModalBody:
        'The post will be deleted from the board and will no longer be visible. This action cannot be reversed.',
    };
  }

  return {
    action: 'Create post?',
    positiveTooltip: 'Create post',
    negativeTooltip: 'Dismiss with no action',
    deleteModalCTA: 'Dismiss feedback',
    deleteModalHeader: 'Are you sure you want to dismiss this feedback?',
    deleteModalBody:
      'The suggested feedback will no longer be visible to you or other users in your workspace. This action cannot be reversed.',
  };
};

const QueueActionsCell = ({ row, boards }: Props) => {
  const [mergeOpen, setMergeOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState<Action | null>();

  const performDelete = async () => {
    setLoadingAction(Action.delete);
    await row.actions.delete();
    setLoadingAction(null);
  };

  const performApprove = async () => {
    setLoadingAction(Action.approve);
    await row.actions.approve();
    setLoadingAction(null);
  };

  const performMerge = async (post: Post) => {
    setLoadingAction(Action.merge);
    await row.actions.merge(post);
    setLoadingAction(null);
  };

  const { action, positiveTooltip, negativeTooltip, ...deleteModalLabels } = getLabels(row);

  return (
    <Popover open={mergeOpen} onOpenChange={setMergeOpen}>
      <VStack className="gap-2">
        <span className="text-secondary-foreground text-base">{action}</span>
        <Flex className="flex-col md:flex-row justify-between items-center gap-2">
          <Tooltip>
            <Tooltip.Trigger asChild>
              <Button
                aria-label={positiveTooltip}
                isLoading={loadingAction === Action.merge || loadingAction === Action.approve}
                iconOnly
                variant="outlined"
                onClick={() => {
                  if (row.item.duplicatePost) {
                    performMerge(row.item.duplicatePost);
                  } else {
                    performApprove();
                  }
                }}>
                <ThumbsUp />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{positiveTooltip}</Tooltip.Content>
          </Tooltip>
          <AlertDialog>
            <AlertDialog.Trigger asChild>
              <div>
                <Tooltip>
                  <Tooltip.Trigger asChild>
                    <Button
                      aria-label={negativeTooltip}
                      disabled={loadingAction === Action.delete}
                      iconOnly
                      variant="outlined"
                      isLoading={loadingAction === Action.delete}>
                      <ThumbsDown />
                    </Button>
                  </Tooltip.Trigger>
                  <Tooltip.Content>{negativeTooltip}</Tooltip.Content>
                </Tooltip>
              </div>
            </AlertDialog.Trigger>
            <AlertDialog.Content>
              <AlertDialog.Header>
                <AlertDialog.Title>{deleteModalLabels.deleteModalHeader}</AlertDialog.Title>
                <AlertDialog.Description>
                  {deleteModalLabels.deleteModalBody}
                </AlertDialog.Description>
              </AlertDialog.Header>
              <AlertDialog.Footer>
                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                <AlertDialog.Action asChild color="error">
                  <Button
                    type="button"
                    isLoading={loadingAction === Action.delete}
                    onClick={performDelete}>
                    {deleteModalLabels.deleteModalCTA}
                  </Button>
                </AlertDialog.Action>
              </AlertDialog.Footer>
            </AlertDialog.Content>
          </AlertDialog>
          <Popover.Anchor>
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <div>
                  <Tooltip>
                    <Tooltip.Trigger asChild>
                      <Button aria-label="More actions" iconOnly variant="outlined">
                        <MoreHorizontal />
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content>More actions</Tooltip.Content>
                  </Tooltip>
                </div>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  label="Add vote to other post"
                  onSelect={() => setMergeOpen(true)}
                />
                {row.item.source !== 'canny' && row.item.duplicatePost && (
                  <DropdownMenu.Item label="Create a new post" onSelect={performApprove} />
                )}
              </DropdownMenu.Content>
            </DropdownMenu>
          </Popover.Anchor>
        </Flex>
      </VStack>
      <Popover.Content align="end" className="p-0 min-w-[300px]">
        <MergePostDropdown
          board={row.item.board}
          boards={boards}
          placeholder="Search all posts..."
          permissionKey="voteOnBehalf"
          onPostSelected={row.actions.merge}
          post={row.item}
        />
      </Popover.Content>
    </Popover>
  );
};

export default QueueActionsCell;
